// useLiveRuns() → { count, overdue, ready }   runs on a node within their timeout, and past it (RESTYLE §6; lib/records liveness)
// Drives the nav mark's scan (count only) and the status bar's "N live · M overdue". From the record: GET
// /v1/jobs?limit=100 every 15 s. While the Analyses page is mounted it shares the split its own chip and lede show
// (useShareLiveRuns: effective states from the stream peeks), so the numbers on that screen agree and the shell runs
// no second, overlapping list query.
//   useShareLiveRuns(split | null)   a page hands its { count, overdue } to the shell while it is mounted
import { useEffect, useMemo, useSyncExternalStore } from "react";
import { useClock, useJobs } from "@/hooks";
import { recordLiveness } from "@/lib/records";

const LIVE_RUNS_LIMIT = 100;
const LIVE_RUNS_POLL_MS = 15_000;

type Split = { count: number; overdue: number };
let shared: Split | null = null;
let sharers = 0;
const listeners = new Set<() => void>();
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => void listeners.delete(cb);
};
const publish = () => listeners.forEach((cb) => cb());

/** split: null while the page's list is still loading (the shell then shows nothing rather than its own count). */
export function useShareLiveRuns(split: Split | null) {
  useEffect(() => {
    sharers++;
    return () => {
      sharers--;
      shared = null;
      publish();
    };
  }, []);
  const count = split?.count;
  const overdue = split?.overdue;
  useEffect(() => {
    shared = count === undefined || overdue === undefined ? null : { count, overdue };
    publish();
  }, [count, overdue]);
}

const snapshot = () => (sharers > 0 ? (shared ?? "pending") : null);

export function useLiveRuns(): { count: number; overdue: number; ready: boolean } {
  const page = useSyncExternalStore(subscribe, snapshot, () => null);
  const own = useJobs(LIVE_RUNS_LIMIT, { refetchMs: LIVE_RUNS_POLL_MS, enabled: page === null });
  const now = useClock(10_000);
  const fromRecords = useMemo(() => {
    let count = 0;
    let overdue = 0;
    for (const j of own.data ?? []) {
      const l = recordLiveness(j, now);
      if (l === "live") count++;
      else if (l === "overdue") overdue++;
    }
    return { count, overdue };
  }, [own.data, now]);
  if (page === "pending") return { count: 0, overdue: 0, ready: false };
  if (page) return { ...page, ready: true };
  return { ...fromRecords, ready: own.isSuccess };
}
